import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import type { FirmDataSecurityResponse } from '@/core/models/firm-settings.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { firmSettingsService } from '@/core/services/firmSettings';
import { InfoHint } from '@/kit/InfoHint';
import { PageHeader } from '@/kit/PageHeader';
import { Toggle } from '@/kit/Toggle';

const TOOLTIP =
  "This is the firm-wide switch. When off, no client's Accounts Report PDF is password\n" +
  "                    protected, even if that client's own Data Security setting is enabled. When on, each client's\n" +
  "                    own setting (under that client's Settings > Data Security) decides whether its PDFs are protected.";

const ADMIN_ONLY = 'Only a firm admin can change this setting. You can see its current state here.';

/** Angular `date: 'medium'` (en-US, local time zone): `MMM d, y, h:mm:ss a`. */
function mediumDate(value: string): string {
  return dayjs(value).locale('en').format('MMM D, YYYY, h:mm:ss A');
}

/**
 * Firm-wide kill switch over the per-client Data Security setting: when off here, no client's Accounts Report PDF is
 * password protected regardless of that client's own setting. Only a firm admin can change it (`canEdit`).
 */
export function DataSecurity() {
  const [isEnabled, setIsEnabled] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [updatedAtUtc, setUpdatedAtUtc] = useState<string | null>(null);
  const isEnabledRef = useRef(false);
  const mounted = useRef(true);

  const apply = (status: FirmDataSecurityResponse) => {
    isEnabledRef.current = status.isEnabled;
    setIsEnabled(status.isEnabled);
    setCanEdit(status.canEdit);
    setUpdatedAtUtc(status.updatedAtUtc);
  };

  useEffect(() => {
    mounted.current = true;
    firmSettingsService.getDataSecurity().then(
      (status) => {
        if (mounted.current) apply(status);
      },
      (error) => notifyError(error),
    );
    return () => {
      mounted.current = false;
    };
  }, []);

  const onToggle = (enabled: boolean) => {
    if (!canEdit) {
      return;
    }
    const previous = isEnabledRef.current;
    isEnabledRef.current = enabled;
    setIsEnabled(enabled);
    setIsSaving(true);
    firmSettingsService.setDataSecurityEnabled(enabled).then(
      (status) => {
        setIsSaving(false);
        if (mounted.current) apply(status);
        notifySuccess(status.isEnabled ? 'Data security enabled for the firm' : 'Data security disabled for the firm');
      },
      (error) => {
        setIsSaving(false);
        if (mounted.current) {
          isEnabledRef.current = previous;
          setIsEnabled(previous);
        }
        notifyError(error);
      },
    );
  };

  return (
    <div className="form wp-100">
      {/* 2 Nov design review: one page header with a single info icon (the firm-wide explanation, plus the admin-only note when read-only); the checkbox is a toggle that saves at once.
          4 Nov: title hidden - the Settings menu this page sits in already shows "Data Security" highlighted, so a second heading here would just repeat it. */}
      <PageHeader title="Data Security" hideTitle>
        <InfoHint text={canEdit ? TOOLTIP : `${TOOLTIP}\n${ADMIN_ONLY}`} />
      </PageHeader>
      <div className="content-body">
        <div className="d-flex align-items-center gap-10 mt-1">
          <Toggle disabled={!canEdit || isSaving} checked={isEnabled} onCheckedChange={onToggle}>
            Enable document Data Security for the firm
          </Toggle>
        </div>

        <div className="info-message primary-message mt-1">
          As per the GDPR (General Data Protection Regulation), Capium provides the option to password protect Accounts
          Report PDFs. This firm-wide switch controls whether that protection can apply at all; each client's own Data
          Security page (under that client's Settings) sets whether it is actually password protected and what the
          master password is.
        </div>

        {updatedAtUtc && <p className="font-size-14 mt-10 mb-0">Last changed {mediumDate(updatedAtUtc)}.</p>}
      </div>
    </div>
  );
}
